<template>
  <CodSheet v-model="isOpen" width="480px">
    <div class="p-5">
      <UAlert color="error" variant="solid" :title="t('subscription.heading')">
        <template #description>
          <template v-if="subscriptionStatus !== 'active'">
            <span v-if="subscriptionStatus === 'trial_expired'">{{ t('subscription.trial_expired') }}</span>
            <span v-else-if="subscriptionStatus === 'expired_contact_support'">{{ t('subscription.expired_contact_support') }}</span>
            <span v-else-if="subscriptionStatus === 'disabled_contact_support'">{{ t('subscription.disabled_contact_support') }}</span>
          </template>

          <template v-else>
            <div v-if="shouldShowDebtAmount()">
              <span>
                <strong>{{ t('subscription.debt_amount', { value: debt.toFormat(), software: 'Konty' }) }}</strong>
                <br>
              </span>
              <span>{{ t('subscription.debt_settle') }} </span>
              <span>{{ t('subscription.debt_invoices') }}</span>
            </div>
            <div v-else>
              <span>{{ t('subscription.debt_settle_reseller', { software: 'Konty' }) }} </span>
            </div>
          </template>
        </template>
      </UAlert>
    </div>

    <template #footer>
      <UButton size="lg"
        v-if="subscriptionStatus !== 'active'"
        color="error"
        variant="solid"
        icon="i-lucide-log-out"
        :label="t('general.log_out')"
        @click="logoutTenant"
      />
      <UButton size="lg"
        v-else
        color="neutral"
        variant="outline"
        :label="t('general.close')"
        @click="hide"
      />
    </template>
  </CodSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAppStore } from '@/stores/useAppStore'

import { AppStorageKey, IAppStorage, ITenant, TenantService, apiService, diContainer } from 'app-core';
import { Money } from 'app-core';
import { CodSheet } from 'app-components';
import moment from 'moment-timezone'

const { t } = useI18n()
const store = useAppStore()
const router = useRouter()

const appStorage = diContainer.resolve<IAppStorage>('appStorage')

const isOpen = ref(false)
const debt = ref(Money.parse(0))
const subscriptionStatus = computed(() => {
  if(store.tenant?.status === 'expired') {
    return store.tenant?.active_tenant_contracts?.length
      ? 'expired_contact_support'
      : 'trial_expired'
  } else if(store.tenant?.status === 'disabled') {
    return 'disabled_contact_support'
  } else {
    return 'active';
  }
})

if(import.meta.env.VITE_ENABLE_BILLING === 'true') {
  debt.value = store?.tenant?.company
    ? Money.parse(store.tenant.company.amount_due)
    : Money.parse(0)

  showDebtWarning()
}

watch(() => store.tenant, async (val, oldVal) => {
  if(import.meta.env.VITE_ENABLE_BILLING !== 'true') return

  debt.value = store?.tenant?.company
    ? Money.parse(store.tenant.company.amount_due)
    : Money.parse(0)

  showDebtWarning()
})

watch(() => store.subscriptionExpired, (val, oldVal) => {
  if(!val) return
  onSubscriptionExpired()
})

async function onSubscriptionExpired() {
  if(subscriptionStatus.value === 'active') {
    await reloadTenant();
  }

  isOpen.value = true
}

function hide() {
  isOpen.value = false
}

async function showDebtWarning() {
  if(debt.value.toUnit() < 2) return

  if(moment().hour() <= 5) return

  const today = moment().format('YYYY-MM-DD')
  const debtWarningSeenDateAt = await appStorage.local().getItem<string>(AppStorageKey.debtWarningSeenDate);

  if(!debtWarningSeenDateAt || today !== debtWarningSeenDateAt) {
    appStorage.local().setItem(AppStorageKey.debtWarningSeenDate, today);
    isOpen.value = true
  }
}

function shouldShowDebtAmount() {
  return store.tenant?.company?.latest_invoice?.path
}

async function reloadTenant(){
  const response = await apiService.get<ITenant>('/me');
  await TenantService.setTenant(response.data);
}

async function logoutTenant() {
  await TenantService.logout();
  router.push({ name: 'tenantLogin' })
}
</script>
